import { createNonce, createSecureHeaders } from "@mcansh/http-helmet";
import {
  ActionFunction,
  data,
  LinksFunction,
  LoaderFunction,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  ClientLoaderFunction,
  isRouteErrorResponse,
  Params,
  useLoaderData,
  useOutletContext,
  useRouteError,
  useRouteLoaderData,
  useSearchParams,
} from "@remix-run/react";
import { captureMessage, captureRemixErrorBoundaryError } from "@sentry/remix";
import cookie from "cookie";
import { useEffect, useRef } from "react";
import { v4 as generateUUID } from "uuid";
import ErrorModal, { ErrorModalProps } from "~/components/ErrorModal";
import NotFound, { links as notFoundLinks } from "~/components/NotFound";
import Page from "~/components/Page";
import ProductAddToCart from "~/components/ProductAddToCart";
import ProductAvailability from "~/components/ProductAvailability";
import ProductBrandLink from "~/components/ProductBrandLink";
import ProductBreadcrumbs from "~/components/ProductBreadcrumbs";
import ProductColorSelector from "~/components/ProductColorSelector";
import ProductDescription from "~/components/ProductDescription";
import ProductError from "~/components/ProductError";
import ProductLinked from "~/components/ProductLinked";
import ProductMedia, {
  mainMediaFrom,
  mediaFrom,
} from "~/components/ProductMedia";
import ProductName from "~/components/ProductName";
import {
  ProductBlockDescription,
  ProductBlocks,
  ProductBlockTrustpilot,
  ProductDetail,
  ProductDetailActions,
  ProductDetailBrand,
  ProductDetailMedia,
  ProductDetailSelector,
  ProductDetailTop,
  ProductDetailTopPilot,
  ProductValuePropIcons,
} from "~/components/ProductPage";
import ProductPrice from "~/components/ProductPrice";
import ProductRelated from "~/components/ProductRelated";
import {
  LargeTrustpilotWidget,
  SimpleTrustpilotWidget,
} from "~/components/TrustpilotWidget";
import { AddToCartActionResponse } from "~/entities/actions/types";
import { StoreLoaderContext, WindowWithGTM } from "~/entities/global/types";
import { ViewProductDetailEvent } from "~/entities/gtm/types";
import { ProductVideo } from "~/entities/product/types";
import { productNameFrom } from "~/entities/product/utils";
import { Variant, VariantImageSizes } from "~/entities/variant/types";
import { selectedVariantFrom } from "~/entities/variant/utils";
import { useHydrated } from "~/hooks/useHydrated";
import { addToCart } from "~/services/shirtspace/cart.server";
import { shirtspaceSessionUserIdFrom } from "~/services/shirtspace/cookies.server";
import { createViewProductDetailGTMEvents } from "~/services/shirtspace/gtm.server";
import { fetchByProductIds } from "~/services/shirtspace/prices.server";
import { fetchByColorAndStyle } from "~/services/shirtspace/productAvailability.server";
import { fetchById } from "~/services/shirtspace/products.server";

const idFrom = (params: Params): string => {
  const id = params.slug?.split("-").shift();

  if (!id) throw new Response(null, { status: 400 });

  return id;
};

const selectedVariantIdFrom = (request: Request) =>
  new URL(request.url).searchParams.get("variant_id");

const hrefStringOrNullFrom = (
  referer: string,
  envURL: string,
  request: Request,
): string | null => {
  try {
    const { pathname: requestPathname } = new URL(request.url);
    const {
      hostname: refererHostname,
      href: refererHref,
      pathname: refererPathname,
    } = new URL(referer);
    const { hostname: originHostname } = new URL(envURL);

    if (refererHostname !== originHostname) {
      return null;
    }

    if (requestPathname === refererPathname) {
      return null;
    }

    return refererHref;
  } catch {
    return null;
  }
};

const headerRefererFrom = (request: Request): string | null => {
  const referer = request.headers.get("referer") ?? "";
  const envURL = process.env.URL ?? "";

  return hrefStringOrNullFrom(referer, envURL, request);
};

const cookieReferrerFrom = (request: Request): string | null => {
  const cookies = cookie.parse(request.headers.get("Set-Cookie") ?? "");
  const referrer = cookies.referrer;
  const envURL = process.env.URL ?? "";

  return hrefStringOrNullFrom(referrer, envURL, request);
};

export const meta: MetaFunction<typeof loader> = ({ data, error }) => {
  if (error) {
    return [
      { title: "Page Not Found | ShirtSpace" },
      {
        name: "description",
        content:
          "We can’t seem to find the page you are looking for. Perhaps you mistyped the URL, or clicked on a broken link? Good thing our menu is nestled at the top of the page, or you can navigate using the links below.",
      },
    ];
  }

  const {
    product: { meta, videos, information },
    variant,
  } = data;

  const variantThumbnailPreloads = (
    Object.values(variant.imageUrls) as VariantImageSizes[]
  ).map((imageSizes) => ({
    tagName: "link",
    as: "image",
    rel: "preload",
    href: imageSizes.thumbnail,
  }));

  const productVideoPreloads = videos.map((video: ProductVideo) => ({
    tagName: "link",
    as: "image",
    rel: "preload",
    href: video.thumbnailUrl,
  }));

  const mainImagePreload = (() => {
    const url = (
      variant.imageUrls.front ??
      variant.imageUrls.side ??
      variant.imageUrls.back
    )?.large;

    if (!url) return null;

    return {
      tagName: "link",
      as: "image",
      rel: "preload",
      href: url,
    };
  })();

  return [
    ...variantThumbnailPreloads,
    ...productVideoPreloads,
    mainImagePreload ?? {},
    {
      title:
        meta.title ??
        information.fullName ??
        "Wholesale Blank Apparel | ShirtSpace",
    },
    {
      name: "description",
      content: meta.description,
    },
  ];
};

export const links: LinksFunction = () => {
  return [...notFoundLinks()];
};

const isSessionStorageUnavailable = () => {
  try {
    if (
      typeof window !== "undefined" &&
      (!sessionStorage || !(sessionStorage instanceof Storage))
    ) {
      throw new Error();
    }

    return false;
  } catch (error) {
    return true;
  }
};

export const ErrorBoundary: React.FC = () => {
  const isHydrated = useHydrated();
  const routeError = useRouteError();
  const outletContext = useOutletContext();
  const loaderData = useRouteLoaderData<typeof loader>("product-detail-page");

  captureMessage("Product detail error boundary seen", {
    extra: {
      routeError,
      outletContext,
    },
  });
  captureRemixErrorBoundaryError(routeError);

  if (isRouteErrorResponse(routeError)) {
    if (routeError.status >= 400) {
      return <NotFound />;
    }
  }

  const errorModalProps: ErrorModalProps = {
    onContinue: () => {
      window.location.reload();
    },
    continueText: "REFRESH",
    cancelText: undefined,
    message: (() => {
      if (isHydrated && isSessionStorageUnavailable()) {
        return `We've detected that cookies are disabled in your browser. This website requires cookies to function properly. Please enable cookies in your browser settings to view prices and add items to your cart. For assistance please contact us at <a class="text-blue" href="tel:18772857606">(877) 285-7606</a>.`;
      } else {
        return `Oops, something unexpected happened. Try refreshing your screen, and if that doesn't work, please contact us at <a class="text-blue" href="tel:18772857606">(877) 285-7606</a>.`;
      }
    })(),
  };

  if (typeof window === "undefined") {
    return (
      <Page className="min-h-[100vh]">
        <ErrorModal {...errorModalProps} />
      </Page>
    );
  }

  return (
    <ProductError
      product={loaderData?.product}
      variantId={loaderData?.variant?.id}
      productAvailability={loaderData?.productAvailability}
      user={loaderData?.data}
    >
      <ErrorModal {...errorModalProps} />
    </ProductError>
  );
};

export const action: ActionFunction = async ({ request }) => {
  const userId = await shirtspaceSessionUserIdFrom(request);
  const formData = await request.formData();
  const variants = Array.from(formData.entries()).reduce(
    (acc, [key, value]) => {
      if (typeof value !== "string" || key === "esid") return acc;
      return {
        ...acc,
        [key]: value,
      };
    },
    {} as Record<string, string>,
  );

  const response = await addToCart(variants, request.headers, userId);

  if (!response) {
    const headers = new Headers();

    return data<AddToCartActionResponse>(
      {
        ok: false,
        errors: [
          { message: "An unknown error occurred while adding to cart." },
        ],
        name: "AddToCart",
      },
      { headers },
    );
  }

  return data<AddToCartActionResponse>(
    {
      ok: true,
      data: response.data,
      name: "AddToCart",
    },
    { headers: response.headers },
  );
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const cookies = cookie.parse(request.headers.get("Cookie") ?? "");
  const currentCartIdCookieValue = cookies["current_cart_id"];
  const userId = await shirtspaceSessionUserIdFrom(request);
  const closeout = request.url.includes("/closeout/").toString();
  const product = await fetchById(idFrom(params), { closeout }, userId);

  if (!product) {
    throw new Response(null, { status: 404 });
  }

  if (product.status !== "active") {
    return redirect(`/${params.mill}/${params.category}`);
  }

  const variant = selectedVariantFrom(selectedVariantIdFrom(request), product);
  const gtmEvents = createViewProductDetailGTMEvents(product.defaultVariant.id);

  const cookieReferrer = cookieReferrerFrom(request);
  const headerReferer = headerRefererFrom(request);
  const referer = cookieReferrer ?? headerReferer;

  const productAvailability = fetchByColorAndStyle(
    variant.color,
    product.styleCode,
    userId,
    currentCartIdCookieValue,
  );

  const relatedProductPrices = fetchByProductIds(
    product.information.relatedProducts.map((product) => product.id),
    userId,
  );

  const initialMainMedia = mainMediaFrom(variant);
  const initialMedia = mediaFrom(variant, product);

  const nonce = createNonce();
  const headers = createSecureHeaders({
    "Content-Security-Policy": {
      "default-src": ["'self'"],
      "script-src": [`'nonce-${nonce}'`],
    },
  });

  const dropReferrerCookie = cookie.serialize("referrer", referer ?? "", {
    path: "/",
    expires: new Date(),
  });
  headers.append("Set-Cookie", dropReferrerCookie);

  const unleashImpressionGTMEvent = {
    event: "unleash_impression",
    unleash_impression: {
      eventType: "getVariant",
      eventId: generateUUID(),
      user_id: userId,
      featureName: "PDP_Remix",
      variant: "remix",
      enabled: true,
    },
  };

  return data(
    {
      product,
      productAvailability,
      variant,
      referer,
      initialMainMedia,
      initialMedia,
      isSignedIn: !!userId,
      gtmEvents,
      nonce,
      relatedProductPrices,
      unleashImpressionGTMEvent,
    },
    {
      headers,
    },
  );
};

let isInitialRender = true;

export const clientLoader: ClientLoaderFunction = async ({
  request,
  serverLoader,
}) => {
  if (isInitialRender) {
    isInitialRender = false;

    const data = await serverLoader<typeof loader>();

    sessionStorage.setItem("productDetail", JSON.stringify(data));

    return data;
  }

  try {
    const cachedData = sessionStorage.getItem("productDetail");

    if (!cachedData) throw new Error("Product page not in session storage.");

    const cache = JSON.parse(cachedData);

    if (!cache.product) {
      throw new Error(
        "Product page found in session storage but is missing product data.",
      );
    }

    const product = cache.product;
    const referer = cache.referer;
    const variant = selectedVariantFrom(
      selectedVariantIdFrom(request),
      product,
    );
    const initialMedia = mediaFrom(variant, product);
    const initialMainMedia = mainMediaFrom(variant);
    const isSignedIn = cache.isSignedIn;
    const nonce = cache.nonce;

    return {
      product,
      productAvailability: undefined,
      variant,
      referer,
      initialMainMedia,
      initialMedia,
      isSignedIn,
      gtmEvents: undefined,
      nonce,
      relatedProductPrices: undefined,
      unleashImpressionGTMEvent: cache.unleashImpressionGTMEvent,
    };
  } catch (error) {
    return await serverLoader();
  }
};

clientLoader.hydrate = true;

const Product = () => {
  const isHydrated = useHydrated();
  const hasPushedGTMEvents = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    productAvailability,
    variant,
    product,
    referer,
    initialMedia,
    initialMainMedia,
    isSignedIn,
    gtmEvents,
    nonce,
    relatedProductPrices,
    unleashImpressionGTMEvent,
  } = useLoaderData<typeof loader>();
  const { user, isGTMReady } = useOutletContext<StoreLoaderContext>();

  const setSelectedVariant = (variant: Variant) => {
    searchParams.set("variant_id", variant.id);
    setSearchParams(searchParams.toString(), {
      replace: true,
      preventScrollReset: true,
    });
  };

  useEffect(() => {
    if (searchParams.get("variant_id") === variant.id) return;
    searchParams.set("variant_id", variant.id);
    history.replaceState(null, "", `?${searchParams.toString()}`);
  }, [variant]);

  useEffect(() => {
    if (!isHydrated || !isGTMReady || !gtmEvents || hasPushedGTMEvents.current)
      return;

    gtmEvents.then((resolvedGtmEvents: ViewProductDetailEvent[]) => {
      resolvedGtmEvents?.forEach((gtmEvent: ViewProductDetailEvent) => {
        (window as WindowWithGTM).dataLayer?.push(gtmEvent);
      });
    });

    hasPushedGTMEvents.current = true;
  }, [gtmEvents, isHydrated, isGTMReady]);

  useEffect(() => {
    const w = window as WindowWithGTM;
    if (
      !isHydrated ||
      !isGTMReady ||
      !unleashImpressionGTMEvent ||
      w.dataLayer?.some((i) => i.event === "unleash_impression")
    )
      return;

    w.dataLayer?.push(unleashImpressionGTMEvent);
  }, [isHydrated, isGTMReady, unleashImpressionGTMEvent]);

  return (
    <Page className="md:mt-2.5 md:flex-col">
      <ProductBreadcrumbs
        product={product}
        referer={referer}
      />
      <ProductDetail>
        <ProductDetailBrand>
          <ProductBrandLink mill={product.mill} />
        </ProductDetailBrand>
        <ProductDetailTop>
          <ProductName name={productNameFrom(product)} />
          <ProductDetailTopPilot isShown={product.reviewable}>
            <SimpleTrustpilotWidget sku={product.styleCode} />
          </ProductDetailTopPilot>
        </ProductDetailTop>
        <ProductDetailMedia>
          <ProductMedia
            initialMedia={initialMedia}
            initialMainMedia={initialMainMedia}
            selectedVariant={variant}
            product={product}
          />
        </ProductDetailMedia>
        <ProductDetailActions>
          <ProductLinked product={product} />
          <ProductPrice product={product} />
          <ProductDetailSelector>
            <ProductColorSelector
              product={product}
              setSelectedVariant={setSelectedVariant}
              selectedVariant={variant}
            />
            <ProductAddToCart
              product={product}
              selectedVariant={variant}
              productAvailability={productAvailability}
              user={user}
            >
              <ProductAvailability
                product={product}
                isSignedIn={isSignedIn}
                selectedVariant={variant}
              />
            </ProductAddToCart>
          </ProductDetailSelector>
        </ProductDetailActions>
      </ProductDetail>
      <ProductValuePropIcons />
      <ProductBlocks>
        <ProductBlockDescription>
          <ProductDescription product={product} />
        </ProductBlockDescription>
        <ProductBlockTrustpilot isShown={product.reviewable}>
          <LargeTrustpilotWidget sku={product.styleCode} />
        </ProductBlockTrustpilot>
      </ProductBlocks>
      <ProductRelated
        relatedProductPrices={relatedProductPrices}
        relatedProducts={product.information.relatedProducts}
      />
      {!user?.doNotSellMyInformation ? (
        <script
          nonce={nonce}
          src="https://webapp.easysize.me/web_app_v1.0/js/easysize.js"
        />
      ) : null}
    </Page>
  );
};

export default Product;
